import React from 'react'
import { Box, Button, Dialog, DialogContent, Typography } from '@mui/material'
import DialogHeader from '../components/DialogHeader'
import { ComponentTicket, useComponentTickets } from '../api/useComponentTickets'
import { advancedColors } from '../theme'
import ComponentNoteButton from './ComponentNoteButton'
import PanelFrame from './PanelFrame'
import { PanelConfigProvider } from './panelConfigContext'
import { PanelPatientProvider } from './panelContext'
import { PanelPreviewProvider } from './panelPreviewContext'
import { PanelDefinition, PanelId } from './registry'

type Props = {
  uid: string
  candidates: PanelDefinition[]
  open: boolean
  onClose: () => void
  onAdd: (panel: PanelId) => void
}

const PreviewCard = ({
  uid,
  panel,
  tickets,
  onSubmitTicket,
  onAdd,
}: {
  uid: string
  panel: PanelDefinition
  tickets: ComponentTicket[]
  onSubmitTicket: (text: string) => void
  onAdd: () => void
}) => (
  <Box
    sx={{
      position: 'relative',
      border: `1px solid ${advancedColors.line}`,
      borderRadius: 3,
      backgroundColor: advancedColors.surface,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}
  >
    <ComponentNoteButton label={panel.title} tickets={tickets} onSubmit={onSubmitTicket} />

    {/* Preview only (ADR-049); maxHeight avoids misrepresenting tall panels. */}
    <Box sx={{ p: 2, maxHeight: 260, overflow: 'auto', backgroundColor: advancedColors.canvas }}>
      <PanelPatientProvider uid={uid}>
        <PanelConfigProvider config={undefined}>
          <PanelPreviewProvider>
            <PanelFrame title={panel.title} chrome={panel.chrome}>
              <panel.Component />
            </PanelFrame>
          </PanelPreviewProvider>
        </PanelConfigProvider>
      </PanelPatientProvider>
    </Box>

    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        p: 1.5,
        borderTop: `1px solid ${advancedColors.line}`,
      }}
    >
      <Typography variant="subtitle2" noWrap>
        {panel.title}
      </Typography>
      <Button size="small" variant="contained" onClick={onAdd}>
        Add to page
      </Button>
    </Box>
  </Box>
)

const ComponentLibraryDialog = ({ uid, candidates, open, onClose, onAdd }: Props) => {
  const { submit, ticketsFor } = useComponentTickets()

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogHeader title="Component library" onClose={onClose} />
      <DialogContent>
        {candidates.length === 0 ? (
          <Typography sx={{ color: advancedColors.inkMuted, py: 4, textAlign: 'center' }}>
            Every component is already on this page.
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2, pb: 1 }}>
            {candidates.map((panel) => (
              <PreviewCard
                key={panel.id}
                uid={uid}
                panel={panel}
                tickets={ticketsFor(panel.id)}
                onSubmitTicket={(text) => submit(panel.id, text)}
                onAdd={() => {
                  onAdd(panel.id)
                  onClose()
                }}
              />
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ComponentLibraryDialog
